import React from 'react';
import { Modal, Form, Input, Select, TimePicker, Switch, InputNumber, Row, Col, Divider, Typography, Checkbox } from 'antd';
import { ClockCircleOutlined, UserOutlined, SettingOutlined, CoffeeOutlined } from '@ant-design/icons';
import { DepartmentCreateDTO, DepartmentUpdateDTO, DepartmentStatus } from '../../../types/department.types';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

interface DepartmentModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: DepartmentCreateDTO | DepartmentUpdateDTO) => void;
  initialValues?: DepartmentCreateDTO | DepartmentUpdateDTO;
  isEdit?: boolean;
  loading?: boolean;
}

const DepartmentModal: React.FC<DepartmentModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  initialValues,
  isEdit = false,
  loading = false
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  React.useEffect(() => {
    if (visible && initialValues) {
      const formValues = {
        ...initialValues,
        fixedShiftStartTime: initialValues.fixedShiftStartTime 
          ? dayjs(initialValues.fixedShiftStartTime, 'HH:mm') 
          : undefined,
        fixedShiftEndTime: initialValues.fixedShiftEndTime 
          ? dayjs(initialValues.fixedShiftEndTime, 'HH:mm') 
          : undefined,
        // String'i array'e çevir
        fixedShiftDays: typeof initialValues.fixedShiftDays === 'string' 
          ? (initialValues.fixedShiftDays as string).split(',').filter((day: string) => day.trim()) 
          : Array.isArray(initialValues.fixedShiftDays) 
            ? initialValues.fixedShiftDays 
            : [],
      };
      form.setFieldsValue(formValues);
      setLateTolerance(initialValues.lateTolerance || false);
    } else if (visible) {
      form.resetFields();
      setLateTolerance(false);
    }
  }, [visible, initialValues, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const formattedValues = {
        ...values,
        fixedShiftStartTime: values.fixedShiftStartTime?.format('HH:mm'),
        fixedShiftEndTime: values.fixedShiftEndTime?.format('HH:mm'),
        // Array'i string'e çevir
        fixedShiftDays: Array.isArray(values.fixedShiftDays) 
          ? values.fixedShiftDays.join(',') 
          : values.fixedShiftDays || '',
      };
      onSubmit(formattedValues);
    } catch (error) {
      console.error('Form validation error:', error);
    }
  };

  const statusOptions = [
    { value: DepartmentStatus.ACTIVE, label: t('departments.active') },
    { value: DepartmentStatus.INACTIVE, label: t('departments.inactive') },
  ];

  const [lateTolerance, setLateTolerance] = React.useState(false);

  return (
    <Modal
      title={
        <div className="d-flex align-items-center">
          <UserOutlined className="me-2" />
          {isEdit ? t('departments.editDepartment') : t('departments.addDepartment')}
        </div>
      }
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={800}
      okText={isEdit ? t('departments.saveDepartment') : t('departments.addDepartment')}
      cancelText={t('departments.cancelDepartment')}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          status: DepartmentStatus.ACTIVE,
          lateTolerance: false,
        }}
      >
        {/* Temel Bilgiler */}
        <div className="mb-4">
          <Title level={5} className="mb-3">
            <UserOutlined className="me-2" />
            Temel Bilgiler
          </Title>
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                name="name"
                label={t('departments.departmentName')}
                rules={[
                  { required: true, message: t('departments.departmentNameError') },
                  { min: 2, message: t('departments.departmentNameError1') },
                  { max: 50, message: t('departments.departmentNameError2') }
                ]}
              >
                <Input size="large" placeholder="Departman adını giriniz" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="status"
                label={t('departments.status')}
                rules={[{ required: true, message: 'Durum seçiniz' }]}
              >
                <Select size="large" options={statusOptions} />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <Divider />

        {/* Vardiya Ayarları */}
        <div className="mb-4">
          <Title level={5} className="mb-3">
            <ClockCircleOutlined className="me-2" />
            Çalışma Saatleri
          </Title>
          <Text type="secondary" className="d-block mb-3">
            Departmanın çalışma saatlerini belirleyebilirsiniz
          </Text>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="fixedShiftStartTime"
                label="Departman Başlangıç Saati"
                rules={[{ required: true, message: 'Başlangıç saati zorunludur' }]}
              >
                <TimePicker
                  size="large"
                  format="HH:mm"
                  placeholder="Başlangıç Saati"
                  className="w-100"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="fixedShiftEndTime"
                label="Departman Bitiş Saati"
                rules={[{ required: true, message: 'Bitiş saati zorunludur' }]}
              >
                <TimePicker
                  size="large"
                  format="HH:mm"
                  placeholder="Bitiş Saati"
                  className="w-100"
                />
              </Form.Item>
            </Col>
          </Row>
          
          {/* Çalışma Günleri */}
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="fixedShiftDays"
                label="Çalışma Günleri"
                rules={[{ required: true, message: 'En az bir çalışma günü seçmelisiniz' }]}
              >
                <Checkbox.Group
                  options={[
                    { label: 'Pazartesi', value: 'Monday' },
                    { label: 'Salı', value: 'Tuesday' },
                    { label: 'Çarşamba', value: 'Wednesday' },
                    { label: 'Perşembe', value: 'Thursday' },
                    { label: 'Cuma', value: 'Friday' },
                    { label: 'Cumartesi', value: 'Saturday' },
                    { label: 'Pazar', value: 'Sunday' },
                  ]}
                  className="d-flex flex-wrap gap-2"
                />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <Divider />

        {/* Geç Kalma Ayarları */}
        <div className="mb-4">
          <Title level={5} className="mb-3">
            <SettingOutlined className="me-2" />
            Geç Kalma Toleransı
          </Title>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="lateTolerance"
                label="Geç Kalma Toleransı"
                valuePropName="checked"
                rules={[{ required: true, message: 'Tolerans durumu seçmelisiniz' }]}
              >
                <div className="d-flex align-items-center">
                  <Switch 
                    onChange={(checked) => {
                      setLateTolerance(checked);
                      form.setFieldsValue({ lateTolerance: checked });
                    }}
                    checked={lateTolerance}
                  />
                </div>
              </Form.Item>
            </Col>
            {lateTolerance && (
              <Col span={12}>
                <Form.Item
                  name="lateTolerationMinutes"
                  label="Tolerans Süresi (Dakika)"
                  rules={[{ required: true, message: 'Tolerans süresi giriniz' }]}
                >
                  <InputNumber
                    size="large"
                    min={1}
                    max={60}
                    placeholder="Dakika"
                    className="w-100"
                    addonAfter="dakika"
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </div>

        <Divider />

        {/* Mola Ayarları */}
        <div className="mb-4">
          <Title level={5} className="mb-3">
            <CoffeeOutlined className="me-2" />
            Mola Ayarları
          </Title>
          <Text type="secondary" className="d-block mb-3">
            Departman için varsayılan mola sürelerini belirleyebilirsiniz
          </Text>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="defaultBreakDuration"
                label="Bir Molanın maksimum süresi"
                rules={[{ required: true, message: 'Varsayılan mola süresi zorunludur' }]}
              >
                <InputNumber
                  size="large"
                  min={5}
                  max={120}
                  placeholder="Dakika"
                  className="w-100"
                  addonAfter="dakika"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="maxBreakDuration"
                label="Günlük Maksimum Mola Süresi"
                rules={[{ required: true, message: 'Maksimum mola süresi zorunludur' }]}
              >
                <InputNumber
                  size="large"
                  min={5}
                  max={240}
                  placeholder="Dakika"
                  className="w-100"
                  addonAfter="dakika"
                />
              </Form.Item>
            </Col>
          </Row>
        </div>
      </Form>
    </Modal>
  );
};

export default DepartmentModal; 